import { useGetAccountsQuery } from "@/modules/account/services";
import type { ICardTransferForm } from "@/modules/transaction/common/types";
import { CardTransferForm } from "@/modules/transaction/components";
import { cardTransferValidationSchema } from "@/modules/transaction/validations/validationSchema";
import { DATE_FORMAT, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import { CloudflareAsset, Currencies, SideSheetPosition, StaticUrl, } from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useState } from "react";

export const CardTransfer = ({ isCopy, isEdit }: IOperationPageProps) => {
  const { t, exists } = useTranslationExists();
  const isDesktop = useMediaQuery();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [isCardNumberInvalid, setIsCardNumberInvalid] = useState<boolean>(false);
  const [isNameUnique, setIsNameUnique] = useState<boolean>(false);
  const { data: accountsList = [] } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const initialValues: ICardTransferForm = {
    transactionNumber: "",
    creditCardNumber: "",
    debitAccount: "",
    scheduleDate: formatDate(new Date(), DATE_FORMAT.YYYY_MM_DD),
    amount: "",
    purpose: "",
    currency: Currencies.AMD,
    commissionAccount: "",
    isSaveAsTemplate: false,
    templateName: "",
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={cardTransferValidationSchema({
          t, accountsList, isCardNumberInvalid, isNameUnique
        })}
      >
        <PageHeader>
          {!isSideBarOpen && exists("transfer.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Card transfer info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <CardTransferForm
          isCopy={isCopy}
          isEdit={isEdit}
          accountsList={accountsList}
          setIsCardNumberInvalid={setIsCardNumberInvalid}
          isNameUnique={isNameUnique}
          setIsNameUnique={setIsNameUnique}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.card.sidebarMessage")}
          </Text>
          <InfoLinks keys={[StaticUrl.CorporateTariffs, StaticUrl.CardTariffs]}/>
        </div>
      </SideSheet>
    </>
  );
};
